import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { ClerkProvider } from "@clerk/clerk-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { RegionProvider } from "@/contexts/RegionContext";
import { AircallProvider } from "@/contexts/AircallContext";
import AircallDock from "@/components/AircallDock";
import { SlackProvider } from "@/contexts/SlackContext";
import SlackDock from "@/components/SlackDock";
import Layout from "@/components/Layout";
import ApplicationSearch from "@/pages/ApplicationSearch";
import DocuSignEnvelopesPage from "@/pages/envelopes/DocuSignEnvelopesPage";
import ApplicationsWorkflow from "@/pages/ApplicationsWorkflow";
import UnderwritingHub from "@/pages/UnderwritingHub";
import WorkflowChecklist from "@/pages/WorkflowChecklist";
import AddBankDetails from "@/pages/AddBankDetails";
import Approvals from "@/pages/Approvals";
import BankDetailsManagement from "@/pages/BankDetailsManagement";
import AddApplication from "@/pages/AddApplication";
import SalesAgentStats from "@/pages/SalesAgentStats";
import SalesAnalysis from "@/pages/SalesAnalysis";
import SalesCalendar from "@/pages/sales/SalesCalendar";
import AuditTrail from "@/pages/AuditTrail";
import Login from "@/pages/Login";
import FundingStats from "@/pages/FundingStats";
import AdminSettings from "@/pages/AdminSettings";
import LegalTimelinesPage from "@/pages/legal/timelines/LegalTimelinesPage";
import LoanDiskProductAudit from "@/pages/admin/LoanDiskProductAudit";
// Collections pages
import DailyCollectionsReport from "@/pages/collections/DailyCollectionsReport";
import LoanRepayments from "@/pages/collections/LoanRepayments";
import CollectionsAnalysis from "@/pages/collections/CollectionsAnalysis";
import BadDebtTracker from "@/pages/collections/BadDebtTracker";
import LoanAgeing from "@/pages/collections/LoanAgeing";
import PortfolioAccounts from "@/pages/collections/PortfolioAccounts";
import ComplaintsRegister from "@/pages/complaints/ComplaintsRegister";
import CustomerFile from "@/pages/collections/CustomerFile";
import CollectionsCalendar from "@/pages/collections/CollectionsCalendar";
import AdminUsers from "@/pages/admin/AdminUsers";
import Home from "@/pages/Home";
import MyProfile from "@/pages/MyProfile";
import MyActions from "@/pages/MyActions";
import Notifications from "@/pages/phone/Notifications";
// Legal pages
import LegalCaseTracker from "@/pages/legal/LegalCaseTracker";
import LegalCaseTrackerV2 from "@/pages/legal/LegalCaseTrackerV2";
import LegalAuditMatrix from "@/pages/legal/audit/LegalAuditMatrix";
import LegalHandlerReport from "@/pages/legal/LegalHandlerReport";
import LegalCalendar from "@/pages/legal/LegalCalendar";
import MailInbox from "@/pages/legal/MailInbox";
import LegalCaseFile from "@/pages/legal/LegalCaseFile";
import LegalPerformanceAnalysis from "@/pages/legal/LegalPerformanceAnalysis";
import CreditPolicyIntelligence from "@/pages/legal/CreditPolicyIntelligence";
import PortfolioAnalysis from "@/pages/portfolio/PortfolioAnalysis";
import PolicyCentre from "@/pages/policy/PolicyCentre";
import DocumentGeneration from "@/pages/legal/DocumentGeneration";
// Open Banking
import OpenBanking from "@/pages/applications/OpenBanking";
import ApplicationsInbox from "@/pages/applications/ApplicationsInbox";
import AccountsInbox from "@/pages/inbox/AccountsInbox";
import LegalInbox from "@/pages/inbox/LegalInbox";
// AUS pages
import AUSDocumentGeneration from "@/pages/aus/DocumentGeneration";
import AUSMidDayBankCheck from "@/pages/aus/MidDayBankCheck";
import LoanRepaymentsAUS from "@/pages/collections/LoanRepaymentsAUS";
import AUSLegalNotices from "@/pages/aus/LegalNotices";
import CapayCustomerAccounts from "@/pages/aus/CapayCustomerAccounts";
import ShortLinkRedirect from "@/pages/ShortLinkRedirect";
import { Loader2 } from "lucide-react";
import { useIsPhone } from "@/hooks/useIsPhone";

// Clerk Provider wrapper with React Router navigation.
// Preview/dev domains use the TEST instance key; the production domain
// (simplyfundedportal.biz) automatically uses the LIVE instance key.
const CLERK_LIVE_DOMAIN = "simplyfundedportal.biz";

function ClerkProviderWithNavigate({ children }) {
  const navigate = useNavigate();

  const host = window.location.hostname;
  const isLiveDomain = host === CLERK_LIVE_DOMAIN || host.endsWith(`.${CLERK_LIVE_DOMAIN}`);
  const publishableKey = isLiveDomain
    ? (process.env.REACT_APP_CLERK_PUBLISHABLE_KEY_LIVE || process.env.REACT_APP_CLERK_PUBLISHABLE_KEY)
    : process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

  if (!publishableKey) {
    console.error("Clerk configuration missing. Check REACT_APP_CLERK_PUBLISHABLE_KEY.");
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-red-500">Clerk configuration error. Please check environment variables.</p>
      </div>
    );
  }

  return (
    <ClerkProvider
      publishableKey={publishableKey}
      routerPush={(to) => navigate(to)}
      routerReplace={(to) => navigate(to, { replace: true })}
      signInUrl="/login"
      afterSignOutUrl="/login"
    >
      {children}
    </ClerkProvider>
  );
}

// Shown when a signed-in Clerk user has no role assigned yet — access must
// be granted by an admin from User Management.
function AwaitingAccess() {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4" data-testid="awaiting-access">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-xl p-8 text-center space-y-4 shadow-sm">
        <div className="mx-auto w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-amber-600" />
        </div>
        <h1 className="text-xl font-semibold text-slate-900">Awaiting access</h1>
        <p className="text-sm text-slate-600">
          You're signed in as <span className="font-medium">{user?.email}</span>, but no role has been
          assigned to your account yet. Ask a portal admin to assign your role in{" "}
          <span className="font-medium">User Management</span>.
        </p>
        <button
          onClick={logout}
          className="text-sm text-blue-600 hover:underline"
          data-testid="awaiting-access-signout"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

// Protected route wrapper
function RequireAuth({ children }) {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Signed in but no role yet — access must be granted by an admin
  if (user && !user.is_dev_bypass && !user.role) {
    return <AwaitingAccess />;
  }

  return children;
}

// Admin route wrapper
function RequireAdmin({ children }) {
  const { isAdmin, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin()) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
}

// Generic permission gate — accepts a single permission string or an array of
// alternatives (OR semantics). Falls back to home when the user can't access.
function RequirePermission({ permission, anyOf, children }) {
  const { hasPermission, hasAnyPermission, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const allowed = anyOf ? hasAnyPermission(anyOf) : hasPermission(permission);
  if (!allowed) {
    return <Navigate to="/" state={{ from: location, reason: "missing-permission" }} replace />;
  }
  return children;
}

// Role gate — Portfolio Analysis is restricted to credit and management roles.
function RequireRole({ anyOf, children }) {
  const { hasAnyRole, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  if (!hasAnyRole(anyOf)) {
    return <Navigate to="/" state={{ from: location, reason: "missing-role" }} replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      {/* Public short-link redirect — customers receive these in SMS */}
      <Route path="/r/:code" element={<ShortLinkRedirect />} />
      
      {/* Protected routes */}
      <Route path="/" element={
        <RequireAuth>
          <Layout />
        </RequireAuth>
      }>
        {/* Applications */}
        <Route index element={<Home />} />
        <Route path="applications" element={<RequirePermission anyOf={["can_view_team_cases", "page_underwriting_hub"]}><UnderwritingHub /></RequirePermission>} />
        <Route path="applications/legacy" element={<RequirePermission anyOf={["can_view_team_cases", "page_underwriting_hub"]}><ApplicationsWorkflow /></RequirePermission>} />
        <Route path="applications/open-banking" element={<RequirePermission anyOf={["can_recommend_credit", "page_open_banking"]}><OpenBanking /></RequirePermission>} />
        <Route path="applications/inbox" element={<RequirePermission anyOf={["can_view_team_cases", "page_applications_inbox"]}><ApplicationsInbox /></RequirePermission>} />
        <Route path="workflow/:appId" element={<WorkflowChecklist />} />
        <Route path="add-bank-details/:appId" element={<AddBankDetails />} />
        <Route path="bank-management/:appId" element={<BankDetailsManagement />} />
        <Route path="add-application" element={<AddApplication />} />
        
        {/* Sales */}
        <Route path="credit-deals" element={<RequirePermission anyOf={["can_recommend_credit", "page_sales"]}><ApplicationSearch /></RequirePermission>} />
        <Route path="credit-deals/envelopes" element={<RequirePermission anyOf={["can_recommend_credit", "page_sales"]}><DocuSignEnvelopesPage /></RequirePermission>} />
        <Route path="sales-calendar" element={<RequirePermission anyOf={["can_recommend_credit", "can_view_reports", "page_sales"]}><SalesCalendar /></RequirePermission>} />
        <Route path="funding-stats" element={<RequirePermission anyOf={["can_view_reports", "page_sales"]}><FundingStats /></RequirePermission>} />
        <Route path="sales-analysis" element={<RequirePermission anyOf={["can_view_reports", "page_sales"]}><SalesAnalysis /></RequirePermission>} />
        <Route path="sales-analytics" element={<RequirePermission anyOf={["can_view_reports", "page_sales"]}><SalesAnalysis /></RequirePermission>} />
        <Route path="sales-analytics/planning" element={<RequirePermission anyOf={["can_view_reports", "page_sales"]}><SalesAnalysis defaultTab="planning" /></RequirePermission>} />
        <Route path="sales-analytics/performance" element={<RequirePermission anyOf={["can_view_reports", "page_sales"]}><SalesAnalysis defaultTab="performance" /></RequirePermission>} />
        <Route path="approvals" element={<RequirePermission anyOf={["can_approve_credit", "page_sales"]}><Approvals /></RequirePermission>} />
        <Route path="credit-approvals" element={<RequirePermission anyOf={["can_approve_credit", "page_sales"]}><Approvals /></RequirePermission>} />
        <Route path="sales-stats" element={<RequirePermission anyOf={["can_view_reports", "page_sales"]}><SalesAgentStats /></RequirePermission>} />
        
        {/* Collections */}
        <Route path="collections/daily-report" element={<RequirePermission permission="can_view_collections"><DailyCollectionsReport /></RequirePermission>} />
        <Route path="collections/loan-repayments" element={<RequirePermission permission="can_view_collections"><LoanRepayments /></RequirePermission>} />
        <Route path="collections/analysis" element={<RequirePermission anyOf={["can_view_collections", "can_view_reports"]}><CollectionsAnalysis /></RequirePermission>} />
        <Route path="collections/bad-debt" element={<RequirePermission anyOf={["can_view_collections", "can_view_reports"]}><BadDebtTracker /></RequirePermission>} />
        <Route path="collections/ageing" element={<RequirePermission permission="can_view_collections"><LoanAgeing /></RequirePermission>} />
        <Route path="collections/ageing/:caseId" element={<RequirePermission permission="can_view_collections"><CustomerFile /></RequirePermission>} />
        <Route path="collections/portfolio-accounts" element={<RequirePermission permission="can_view_collections"><PortfolioAccounts /></RequirePermission>} />
        <Route path="collections/calendar" element={<RequirePermission permission="can_view_collections"><CollectionsCalendar /></RequirePermission>} />
        <Route path="collections/complaints" element={<RequirePermission permission="can_view_collections"><ComplaintsRegister /></RequirePermission>} />
        <Route path="legal/complaints" element={<RequirePermission anyOf={["can_view_collections", "page_legal"]}><ComplaintsRegister /></RequirePermission>} />
        <Route path="admin/users" element={<RequirePermission permission="can_manage_users"><AdminUsers /></RequirePermission>} />
        <Route path="admin/roles" element={<Navigate to="/admin/users?tab=roles" replace />} />
        <Route path="profile" element={<MyProfile />} />
        <Route path="my-actions" element={<MyActions />} />
        <Route path="notifications" element={<Notifications />} />
        
        {/* Legal */}
        {/* Phase 1 — new tracker uses the polished CustomerFile.jsx for the case
            view (passes ?source=legal via the row click). Old V1 routes kept
            as `*-legacy` so ops can fall back if something breaks. */}
        <Route path="legal/case-tracker" element={<RequirePermission anyOf={["can_view_collections", "page_legal"]}><LegalCaseTrackerV2 /></RequirePermission>} />
        <Route path="legal/audit-matrix" element={<RequirePermission anyOf={["can_view_collections", "page_legal"]}><LegalAuditMatrix /></RequirePermission>} />
        <Route path="legal/handler-report" element={<RequirePermission anyOf={["can_view_collections", "page_legal"]}><LegalHandlerReport /></RequirePermission>} />
        <Route path="legal/process-settings" element={<Navigate to="/legal/timelines" replace />} />
        <Route path="legal/timelines" element={<RequirePermission anyOf={["legal_head", "can_configure_system", "page_legal"]}><LegalTimelinesPage /></RequirePermission>} />
        <Route path="legal/calendar" element={<RequirePermission anyOf={["can_view_collections", "page_legal"]}><LegalCalendar /></RequirePermission>} />
        <Route path="legal/mail-inbox" element={<RequirePermission anyOf={["can_view_collections", "page_legal"]}><MailInbox /></RequirePermission>} />
        <Route path="legal/inbox" element={<RequirePermission anyOf={["page_legal", "legal_head"]}><LegalInbox /></RequirePermission>} />
        <Route path="collections/inbox" element={<RequirePermission permission="can_view_collections"><AccountsInbox /></RequirePermission>} />
        <Route path="legal/case/:caseId" element={<RequirePermission anyOf={["can_view_collections", "page_legal"]}><CustomerFile /></RequirePermission>} />
        <Route path="legal/case-tracker-legacy" element={<RequirePermission anyOf={["can_view_collections", "page_legal"]}><LegalCaseTracker /></RequirePermission>} />
        <Route path="legal/case-legacy/:caseId" element={<RequirePermission anyOf={["can_view_collections", "page_legal"]}><LegalCaseFile /></RequirePermission>} />
        <Route path="legal/performance" element={<RequirePermission anyOf={["can_view_reports", "page_legal"]}><LegalPerformanceAnalysis /></RequirePermission>} />
        <Route path="legal/credit-policy" element={<RequirePermission anyOf={["can_view_reports", "page_legal"]}><CreditPolicyIntelligence /></RequirePermission>} />
        <Route path="portfolio" element={<RequireRole anyOf={["Admin", "Credit Manager", "Credit TL", "Credit Underwriter", "Collections Manager"]}><PortfolioAnalysis /></RequireRole>} />
        <Route path="policy-centre" element={<RequirePermission anyOf={["page_policy_centre", "can_edit_policy", "can_approve_policy"]}><PolicyCentre /></RequirePermission>} />
        <Route path="legal/documents" element={<RequirePermission anyOf={["can_recommend_credit", "page_onboarding_centre"]}><DocumentGeneration /></RequirePermission>} />
        <Route path="legal/onboarding-centre" element={<RequirePermission anyOf={["can_recommend_credit", "page_onboarding_centre"]}><DocumentGeneration /></RequirePermission>} />
        
        {/* AUS Routes */}
        <Route path="aus/legal/documents" element={<RequirePermission permission="can_recommend_credit"><AUSDocumentGeneration /></RequirePermission>} />
        <Route path="aus/collections/bank-check" element={<RequirePermission permission="can_view_collections"><AUSMidDayBankCheck /></RequirePermission>} />
        <Route path="aus/collections/loan-repayments" element={<RequirePermission permission="can_view_collections"><LoanRepaymentsAUS /></RequirePermission>} />
        <Route path="aus/collections/inbox" element={<RequirePermission permission="can_view_collections"><AccountsInbox /></RequirePermission>} />
        <Route path="aus/legal/inbox" element={<RequirePermission anyOf={["page_legal", "legal_head"]}><LegalInbox /></RequirePermission>} />
        <Route path="aus/collections/calendar" element={<RequirePermission permission="can_view_collections"><CollectionsCalendar /></RequirePermission>} />
        <Route path="aus/collections/ageing" element={<RequirePermission permission="can_view_collections"><LoanAgeing /></RequirePermission>} />
        <Route path="aus/collections/ageing/:caseId" element={<RequirePermission permission="can_view_collections"><CustomerFile /></RequirePermission>} />
        <Route path="aus/collections/portfolio-accounts" element={<RequirePermission permission="can_view_collections"><PortfolioAccounts /></RequirePermission>} />
        <Route path="aus/onboarding-centre" element={<RequirePermission permission="can_recommend_credit"><AUSDocumentGeneration /></RequirePermission>} />
        <Route path="aus/legal/notices" element={<RequirePermission permission="can_view_collections"><AUSLegalNotices /></RequirePermission>} />
        <Route path="aus/collections/capay-accounts" element={<RequirePermission permission="can_view_collections"><CapayCustomerAccounts /></RequirePermission>} />
        
        {/* Admin */}
        <Route path="audit-trail" element={<RequirePermission permission="can_view_reports"><AuditTrail /></RequirePermission>} />
        <Route path="settings" element={<RequirePermission permission="can_configure_system"><AdminSettings /></RequirePermission>} />
        <Route path="admin/loandisk-products" element={<RequirePermission permission="can_configure_system"><LoanDiskProductAudit /></RequirePermission>} />
        <Route path="admin/xero" element={<Navigate to="/settings?tab=xero" replace />} />
      </Route>
    </Routes>
  );
}

function App() {
  const isPhone = useIsPhone();
  return (
    <div className="App">
      <BrowserRouter>
        <ClerkProviderWithNavigate>
          <AuthProvider>
            <RegionProvider>
              <AircallProvider>
                <SlackProvider>
                  <AppRoutes />
                  <AircallDock />
                  <SlackDock />
                </SlackProvider>
              </AircallProvider>
            </RegionProvider>
          </AuthProvider>
        </ClerkProviderWithNavigate>
      </BrowserRouter>
      <Toaster position={isPhone ? "top-center" : "top-right"} richColors offset={isPhone ? "calc(3.5rem + env(safe-area-inset-top, 0px))" : 16} />
    </div>
  );
}

export default App;
